/**
 * CheckboxField — shared types, Zod prop schema, styling constants and
 * helpers.
 *
 * A checkbox with a visible label. The label is an ordered list of segments,
 * each plain text or a link, so a consent sentence can link to the documents
 * it names. Pressing the label text toggles the box; pressing a link opens it
 * and never toggles. Consents start unchecked.
 *
 * Source: Figma file hk9NaJrKRwkoGmmZ5rxbsE, page v7, "Web/Checkbox field"
 * (node 47:5500). The v7 sheet was drawn from this package's code rather than
 * the UI Design System library, so its variable names stand until design adds
 * them to the library.
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { spacing } from '../../elements/spacing';
import { states } from '../../elements/states';
import { typography } from '../../elements/typography';
import type { TypeStyle } from '../../elements/typography';
import {
  CHECKBOX_RADIUS,
  CHECKBOX_SIZE,
  CHECKBOX_TICK_ICON,
  CHECKBOX_TICK_SIZE,
} from '../Checkbox/Checkbox';

export const checkboxFieldLabelSegmentSchema = z.object({
  text: z.string(),
  /** Renders the segment as a link. Only `https:` URLs and relative paths are honoured. */
  href: z.string().optional(),
  /** Domain action id for opening this link (ADR-081); defaults to a generic link id. */
  actionId: z.string().optional(),
});

export type CheckboxFieldLabelSegment = z.infer<typeof checkboxFieldLabelSegmentSchema>;

/** Catalog-facing schema: data props only; callbacks are platform props. */
export const checkboxFieldPropsSchema = z.object({
  labelSegments: z.array(checkboxFieldLabelSegmentSchema).default([]),
  checked: z.boolean().default(false),
  disabled: z.boolean().default(false),
  errorText: z.string().optional(),
  /** Overrides the accessible name, which otherwise is the label text. */
  accessibilityLabel: z.string().optional(),
  testID: z.string().optional(),
});

export type CheckboxFieldProps = z.infer<typeof checkboxFieldPropsSchema>;

/**
 * True when a segment's href may be rendered as a link: an `https:` URL or a
 * relative path. Anything else (`javascript:`, `data:`, `http:`, a
 * protocol-relative `//host`) renders as plain text. Control characters and
 * spaces are removed first, because browsers ignore them inside a scheme.
 */
export function isSafeCheckboxFieldHref(href: string | undefined): href is string {
  if (href === undefined) return false;
  // eslint-disable-next-line no-control-regex
  const compact = href.replace(/[\u0000-\u0020\u007f]/g, '');
  // Browsers read `\` as `/` in a URL, so `/\host` and `\/host` are
  // protocol-relative too.
  if (compact === '' || compact.replace(/\\/g, '/').startsWith('//')) return false;
  const scheme = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(compact);
  if (!scheme) return true;
  return scheme[1]?.toLowerCase() === 'https';
}

/** The label's plain text, used as the fallback accessible name. */
export function checkboxFieldLabelText(segments: readonly CheckboxFieldLabelSegment[]): string {
  return segments.map((segment) => segment.text).join('');
}

// ---------------------------------------------------------------------------
// Styling constants — all derived from design-system elements
// ---------------------------------------------------------------------------

export const CHECKBOX_FIELD_BOX_SIZE = CHECKBOX_SIZE;
export const CHECKBOX_FIELD_BOX_RADIUS = CHECKBOX_RADIUS;
export const CHECKBOX_FIELD_TICK_ICON = CHECKBOX_TICK_ICON;
export const CHECKBOX_FIELD_TICK_SIZE = CHECKBOX_TICK_SIZE;
export const CHECKBOX_FIELD_FOCUS_RING_RADIUS = radius.lg;
export const CHECKBOX_FIELD_ROW_GAP = spacing.sm;
export const CHECKBOX_FIELD_ERROR_GAP = spacing.xs;
export const CHECKBOX_FIELD_ERROR_INDENT = spacing.xxl;
export const CHECKBOX_FIELD_LABEL_TYPOGRAPHY: TypeStyle = typography.b3Regular;
export const CHECKBOX_FIELD_LABEL_COLOR = colors.text.primary;

export interface CheckboxFieldState {
  checked: boolean;
  disabled: boolean;
  hasError: boolean;
  hovered: boolean;
  focused: boolean;
}

export interface CheckboxFieldBoxStyle {
  background: string;
  border: string;
  borderWidth: number;
  tick: string;
}

export function checkboxFieldBoxStyleFor({
  checked,
  disabled,
  hasError,
  hovered,
  focused,
}: CheckboxFieldState): CheckboxFieldBoxStyle {
  // v7 draws no Disabled state; it follows the other fields' Disabled
  // treatment, with a tick dark enough to read on the muted fill.
  if (disabled) {
    return {
      background: colors.background.secondary,
      border: colors.stroke.divider,
      borderWidth: states.borderWidth.default,
      tick: colors.icon.tertiary,
    };
  }
  // v7's `stroke/alert` error border; `button.critical` holds the same colour
  // as `stroke.alert`.
  if (hasError) {
    return {
      background: checked ? colors.background.dark : colors.background.container,
      border: colors.button.critical,
      borderWidth: states.borderWidth.default,
      tick: colors.icon.inverted,
    };
  }
  if (checked) {
    return {
      background: colors.background.dark,
      border: colors.stroke.primary,
      borderWidth: states.borderWidth.default,
      tick: colors.icon.inverted,
    };
  }
  return {
    background: colors.background.container,
    border: hovered || focused ? colors.stroke.primary : colors.stroke.input,
    borderWidth: hovered ? states.borderWidth.selected : states.borderWidth.default,
    tick: colors.icon.inverted,
  };
}
